// EkranPro player — offline, dependency-free i18n.
//
// All translations are BUNDLED (no network fetch) so the player works fully
// offline on low-power TVs (CLAUDE.md §2). The active locale is resolved once at
// load from localStorage → navigator.language → 'tr'.
//
// NOTE: en/de/ar/ru currently mirror the Turkish text — they are placeholders to
// be translated in a later step. The keys + interpolation contract are stable.

export const LOCALES = ['tr', 'en', 'de', 'ar', 'ru'] as const;
export type Locale = (typeof LOCALES)[number];

const LANG_KEY = 'ekp_lang';

// Turkish source of truth. en/de/ar/ru are copies for now (translated later).
const tr: Record<string, string> = {
  // Defaults
  screenNameDefault: 'Ekran',

  // Connection status
  connOnline: 'Bağlı',
  connOffline: 'Bağlantı yok',
  connecting: 'Sunucuya bağlanılıyor…',

  // Setup wizard — shared
  setupGoBack: '← Eşleştirmeye dön',
  setupContinueBrowser: 'Şimdilik tarayıcıda devam et →',

  // Setup wizard — APK card (Android TV)
  setupTvAppTitle: '📺 Televizyon uygulaması',
  setupTvAppSub:
    'En iyi deneyim için EkranPro uygulamasını kurun:<br/>tam ekran, açılışta otomatik başlar, internet kesilse de oynatır.',
  setupDownloadApk: '⬇ Uygulamayı İndir (.apk)',
  setupStepOpen: 'İndir bitince <b>“Aç”a</b> bas',
  setupStepAllow: '“Bilinmeyen kaynaklara izin ver” çıkarsa → <b>İzin ver</b>',
  setupStepInstall: '<b>Kur</b> → <b>Aç</b> → eşleştirme kodu ekranı gelir',
  setupNoteAndroidTv:
    'Yalnızca <b>Android TV / Google TV</b> (TCL, Mi Box, Chromecast vb.) destekler. Tarayıcısı olmayan TV\'de <b>“Downloader”</b> uygulamasına şu adresi yazın:',

  // Setup wizard — browser-only TV (Tizen/webOS)
  setupTvDetectedTitle: '📺 Televizyon algılandı',
  setupTvDetectedSub:
    'Bu TV (Samsung/LG) uygulama kurulumunu desteklemez; tarayıcıda tam ekran çalışır:',
  setupStepRemoteOk:
    'Kumandada <b>OK / Enter</b> tuşuna bas → adres çubuğu kaybolur, tam ekran',
  setupStepAddHome: 'veya tarayıcı menüsünden <b>“Ana ekrana ekle”</b>',
  setupNoteContinue: 'Devam edince eşleştirme kodu görünür; kodu panele girin.',

  // Pairing screen
  pairingLabel: 'EŞLEŞTİRME KODU',
  pairingTimer: 'Bu kod <span id="ttl">--</span> sn sonra yenilenir',
  pairingHint: 'Yönetim panelinde <b>Ekran Bağla</b> alanına bu kodu girin.',
  pairingInstall: '📺 Televizyonda mı kuruyorsunuz? Uygulamayı kurun',

  // Idle status
  idleBroadcastStartsOn: 'Yayın {date} tarihinde başlayacak',
  idleBroadcastExpired: 'Yayın süresi doldu',
  idlePlaylistPreparing: 'Playlist hazırlanıyor…',
  idleWaitingForBroadcast: 'Yayın bekleniyor…',

  // Subscription lock (lapsed trial / suspended)
  subscriptionLockedTitle: 'Pro sürüme geçin',
  subscriptionLockedBody:
    'Deneme süreniz sona erdi. Yayına devam etmek için yöneticinizle iletişime geçin veya paneldeki “Planlar” bölümünden aboneliğinizi başlatın.',

  // Idle controls
  resetLabel: 'Sıfırla',
  resetTitle: 'Eşleştirmeyi kaldır (test)',

  // Identify overlay
  identifyHere: 'Bu ekran burada',

  // Wi-Fi card
  wifiTitle: '📶 Ücretsiz Wi-Fi',
  wifiNetwork: 'Ağ',
  wifiPassword: 'Şifre',
  wifiScanHint: 'Kamerayla QR\'ı okutun',

  // Prayer times
  prayerHeading: 'Namaz Vakitleri',
  prayerFajr: 'İmsak',
  prayerSunrise: 'Güneş',
  prayerDhuhr: 'Öğle',
  prayerAsr: 'İkindi',
  prayerMaghrib: 'Akşam',
  prayerIsha: 'Yatsı',

  // Menu board
  menuOpenOnPhone: 'Menüyü telefonunda aç',

  // Broadcast
  emergencyLabel: '⚠ ACİL DURUM',
};

export const messages: Record<Locale, Record<string, string>> = {
  tr,
  en: {
    screenNameDefault: "Screen",
    connOnline: "Connected",
    connOffline: "No connection",
    connecting: "Connecting to server…",
    setupGoBack: "← Back to pairing",
    setupContinueBrowser: "Continue in browser for now →",
    setupTvAppTitle: "📺 TV app",
    setupTvAppSub: "For the best experience, install the EkranPro app:<br/>full screen, starts automatically on boot, keeps playing even if the internet drops.",
    setupDownloadApk: "⬇ Download the App (.apk)",
    setupStepOpen: "When the download finishes, tap <b>“Open”</b>",
    setupStepAllow: "If “Allow from unknown sources” appears → <b>Allow</b>",
    setupStepInstall: "<b>Install</b> → <b>Open</b> → the pairing code screen appears",
    setupNoteAndroidTv: "Only <b>Android TV / Google TV</b> (TCL, Mi Box, Chromecast, etc.) are supported. On a TV without a browser, enter this address in the <b>“Downloader”</b> app:",
    setupTvDetectedTitle: "📺 TV detected",
    setupTvDetectedSub: "This TV (Samsung/LG) does not support app installation; it runs full screen in the browser:",
    setupStepRemoteOk: "Press <b>OK / Enter</b> on the remote → the address bar disappears, full screen",
    setupStepAddHome: "or <b>“Add to home screen”</b> from the browser menu",
    setupNoteContinue: "Once you continue, the pairing code appears; enter the code in the panel.",
    pairingLabel: "PAIRING CODE",
    pairingTimer: "This code refreshes in <span id=\"ttl\">--</span> sec",
    pairingHint: "Enter this code in the <b>Connect Screen</b> field of the admin panel.",
    pairingInstall: "📺 Setting up on a TV? Install the app",
    idleBroadcastStartsOn: "Broadcast will start on {date}",
    idleBroadcastExpired: "Broadcast has expired",
    idlePlaylistPreparing: "Playlist is being prepared…",
    idleWaitingForBroadcast: "Waiting for broadcast…",
    subscriptionLockedTitle: "Upgrade to Pro",
    subscriptionLockedBody:
      "Your trial has ended. To keep broadcasting, contact your administrator or start your subscription from the “Plans” section in the panel.",
    resetLabel: "Reset",
    resetTitle: "Remove pairing (test)",
    identifyHere: "This screen is here",
    wifiTitle: "📶 Free Wi-Fi",
    wifiNetwork: "Network",
    wifiPassword: "Password",
    wifiScanHint: "Scan the QR with your camera",
    prayerHeading: "Prayer Times",
    prayerFajr: "Fajr",
    prayerSunrise: "Sunrise",
    prayerDhuhr: "Dhuhr",
    prayerAsr: "Asr",
    prayerMaghrib: "Maghrib",
    prayerIsha: "Isha",
    menuOpenOnPhone: "Open the menu on your phone",
    emergencyLabel: "⚠ EMERGENCY",
  },
  de: {
    screenNameDefault: "Bildschirm",
    connOnline: "Verbunden",
    connOffline: "Keine Verbindung",
    connecting: "Verbindung zum Server…",
    setupGoBack: "← Zurück zur Kopplung",
    setupContinueBrowser: "Vorerst im Browser fortfahren →",
    setupTvAppTitle: "📺 Fernseh-App",
    setupTvAppSub: "Für das beste Erlebnis installieren Sie die EkranPro-App:<br/>Vollbild, startet automatisch beim Hochfahren, läuft auch ohne Internet.",
    setupDownloadApk: "⬇ App herunterladen (.apk)",
    setupStepOpen: "Nach dem Download auf <b>„Öffnen“</b> tippen",
    setupStepAllow: "Falls „Unbekannte Quellen zulassen“ erscheint → <b>Zulassen</b>",
    setupStepInstall: "<b>Installieren</b> → <b>Öffnen</b> → der Kopplungscode-Bildschirm erscheint",
    setupNoteAndroidTv: "Unterstützt nur <b>Android TV / Google TV</b> (TCL, Mi Box, Chromecast usw.). Auf einem TV ohne Browser geben Sie in der App <b>„Downloader“</b> folgende Adresse ein:",
    setupTvDetectedTitle: "📺 Fernseher erkannt",
    setupTvDetectedSub: "Dieser TV (Samsung/LG) unterstützt keine App-Installation; er läuft im Vollbild-Browser:",
    setupStepRemoteOk: "Auf der Fernbedienung <b>OK / Enter</b> drücken → die Adressleiste verschwindet, Vollbild",
    setupStepAddHome: "oder im Browser-Menü <b>„Zum Startbildschirm hinzufügen“</b>",
    setupNoteContinue: "Nach dem Fortfahren erscheint der Kopplungscode; geben Sie ihn im Panel ein.",
    pairingLabel: "KOPPLUNGSCODE",
    pairingTimer: "Dieser Code wird in <span id=\"ttl\">--</span> Sek. erneuert",
    pairingHint: "Geben Sie diesen Code im Verwaltungspanel unter <b>Bildschirm verbinden</b> ein.",
    pairingInstall: "📺 Installieren Sie auf einem Fernseher? Installieren Sie die App",
    idleBroadcastStartsOn: "Die Ausstrahlung beginnt am {date}",
    idleBroadcastExpired: "Ausstrahlungszeitraum abgelaufen",
    idlePlaylistPreparing: "Playlist wird vorbereitet…",
    idleWaitingForBroadcast: "Warten auf Ausstrahlung…",
    subscriptionLockedTitle: "Auf Pro upgraden",
    subscriptionLockedBody:
      "Ihre Testphase ist abgelaufen. Um weiter zu senden, wenden Sie sich an Ihren Administrator oder starten Sie Ihr Abo im Bereich „Tarife“ im Panel.",
    resetLabel: "Zurücksetzen",
    resetTitle: "Kopplung entfernen (Test)",
    identifyHere: "Dieser Bildschirm ist hier",
    wifiTitle: "📶 Kostenloses WLAN",
    wifiNetwork: "Netzwerk",
    wifiPassword: "Passwort",
    wifiScanHint: "Scannen Sie den QR-Code mit der Kamera",
    prayerHeading: "Gebetszeiten",
    prayerFajr: "Fadschr",
    prayerSunrise: "Sonnenaufgang",
    prayerDhuhr: "Dhuhr",
    prayerAsr: "Asr",
    prayerMaghrib: "Maghrib",
    prayerIsha: "Ischa",
    menuOpenOnPhone: "Menü auf dem Handy öffnen",
    emergencyLabel: "⚠ NOTFALL",
  },
  ar: {
    screenNameDefault: "شاشة",
    connOnline: "متصل",
    connOffline: "لا يوجد اتصال",
    connecting: "جارٍ الاتصال بالخادم…",
    setupGoBack: "→ العودة إلى الإقران",
    setupContinueBrowser: "← المتابعة في المتصفح الآن",
    setupTvAppTitle: "📺 تطبيق التلفزيون",
    setupTvAppSub: "للحصول على أفضل تجربة، ثبّت تطبيق EkranPro:<br/>ملء الشاشة، يبدأ تلقائيًا عند التشغيل، ويعمل حتى عند انقطاع الإنترنت.",
    setupDownloadApk: "⬇ تنزيل التطبيق (.apk)",
    setupStepOpen: "بعد انتهاء التنزيل اضغط <b>«فتح»</b>",
    setupStepAllow: "إذا ظهر «السماح من مصادر غير معروفة» → <b>اسمح</b>",
    setupStepInstall: "<b>ثبّت</b> ← <b>افتح</b> ← تظهر شاشة رمز الإقران",
    setupNoteAndroidTv: "يدعم فقط <b>Android TV / Google TV</b> (TCL، Mi Box، Chromecast، إلخ). على التلفزيون الذي لا متصفح له، اكتب هذا العنوان في تطبيق <b>«Downloader»</b>:",
    setupTvDetectedTitle: "📺 تم اكتشاف التلفزيون",
    setupTvDetectedSub: "هذا التلفزيون (Samsung/LG) لا يدعم تثبيت التطبيقات؛ يعمل بملء الشاشة في المتصفح:",
    setupStepRemoteOk: "اضغط <b>OK / Enter</b> على جهاز التحكم ← يختفي شريط العنوان وتصبح ملء الشاشة",
    setupStepAddHome: "أو من قائمة المتصفح <b>«إضافة إلى الشاشة الرئيسية»</b>",
    setupNoteContinue: "بعد المتابعة يظهر رمز الإقران؛ أدخل الرمز في اللوحة.",
    pairingLabel: "رمز الإقران",
    pairingTimer: "يتجدد هذا الرمز بعد <span id=\"ttl\">--</span> ثانية",
    pairingHint: "أدخل هذا الرمز في حقل <b>ربط الشاشة</b> في لوحة الإدارة.",
    pairingInstall: "📺 هل تثبّت على التلفزيون؟ ثبّت التطبيق",
    idleBroadcastStartsOn: "يبدأ البث في {date}",
    idleBroadcastExpired: "انتهت مدة البث",
    idlePlaylistPreparing: "جارٍ تجهيز قائمة التشغيل…",
    idleWaitingForBroadcast: "في انتظار البث…",
    subscriptionLockedTitle: "الترقية إلى Pro",
    subscriptionLockedBody:
      "انتهت فترتك التجريبية. لمتابعة البث، تواصل مع المسؤول أو ابدأ اشتراكك من قسم «الخطط» في اللوحة.",
    resetLabel: "إعادة ضبط",
    resetTitle: "إلغاء الإقران (اختبار)",
    identifyHere: "هذه الشاشة هنا",
    wifiTitle: "📶 Wi-Fi مجاني",
    wifiNetwork: "الشبكة",
    wifiPassword: "كلمة المرور",
    wifiScanHint: "امسح رمز QR بالكاميرا",
    prayerHeading: "مواقيت الصلاة",
    prayerFajr: "الفجر",
    prayerSunrise: "الشروق",
    prayerDhuhr: "الظهر",
    prayerAsr: "العصر",
    prayerMaghrib: "المغرب",
    prayerIsha: "العشاء",
    menuOpenOnPhone: "افتح القائمة على هاتفك",
    emergencyLabel: "⚠ حالة طارئة",
  },
  ru: {
    screenNameDefault: "Экран",
    connOnline: "Подключено",
    connOffline: "Нет соединения",
    connecting: "Подключение к серверу…",
    setupGoBack: "← Назад к привязке",
    setupContinueBrowser: "Пока продолжить в браузере →",
    setupTvAppTitle: "📺 Приложение для телевизора",
    setupTvAppSub: "Для лучшего результата установите приложение EkranPro:<br/>полный экран, автозапуск при включении, работает даже без интернета.",
    setupDownloadApk: "⬇ Скачать приложение (.apk)",
    setupStepOpen: "После загрузки нажмите <b>«Открыть»</b>",
    setupStepAllow: "Если появится «Разрешить из неизвестных источников» → <b>Разрешить</b>",
    setupStepInstall: "<b>Установить</b> → <b>Открыть</b> → появится экран с кодом привязки",
    setupNoteAndroidTv: "Поддерживается только <b>Android TV / Google TV</b> (TCL, Mi Box, Chromecast и т.д.). На ТВ без браузера введите этот адрес в приложении <b>«Downloader»</b>:",
    setupTvDetectedTitle: "📺 Телевизор обнаружен",
    setupTvDetectedSub: "Этот ТВ (Samsung/LG) не поддерживает установку приложений; работает в браузере на весь экран:",
    setupStepRemoteOk: "Нажмите <b>OK / Enter</b> на пульте → адресная строка исчезнет, полный экран",
    setupStepAddHome: "или через меню браузера <b>«Добавить на главный экран»</b>",
    setupNoteContinue: "После продолжения появится код привязки; введите код в панель.",
    pairingLabel: "КОД ПРИВЯЗКИ",
    pairingTimer: "Этот код обновится через <span id=\"ttl\">--</span> сек",
    pairingHint: "Введите этот код в поле <b>Подключить экран</b> в панели управления.",
    pairingInstall: "📺 Устанавливаете на телевизоре? Установите приложение",
    idleBroadcastStartsOn: "Трансляция начнётся {date}",
    idleBroadcastExpired: "Срок трансляции истёк",
    idlePlaylistPreparing: "Плейлист готовится…",
    idleWaitingForBroadcast: "Ожидание трансляции…",
    subscriptionLockedTitle: "Перейти на Pro",
    subscriptionLockedBody:
      "Ваш пробный период закончился. Чтобы продолжить трансляцию, свяжитесь с администратором или оформите подписку в разделе «Тарифы» на панели.",
    resetLabel: "Сбросить",
    resetTitle: "Удалить привязку (тест)",
    identifyHere: "Этот экран здесь",
    wifiTitle: "📶 Бесплатный Wi-Fi",
    wifiNetwork: "Сеть",
    wifiPassword: "Пароль",
    wifiScanHint: "Отсканируйте QR камерой",
    prayerHeading: "Время намаза",
    prayerFajr: "Фаджр",
    prayerSunrise: "Восход",
    prayerDhuhr: "Зухр",
    prayerAsr: "Аср",
    prayerMaghrib: "Магриб",
    prayerIsha: "Иша",
    menuOpenOnPhone: "Открыть меню на телефоне",
    emergencyLabel: "⚠ ЭКСТРЕННАЯ СИТУАЦИЯ",
  },
};

function detectLocale(): Locale {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (saved && (LOCALES as readonly string[]).includes(saved)) {
      return saved as Locale;
    }
  } catch {
    /* storage unavailable — fall through */
  }
  const nav = (navigator.language || '').slice(0, 2).toLowerCase();
  if ((LOCALES as readonly string[]).includes(nav)) return nav as Locale;
  return 'tr';
}

export const locale: Locale = detectLocale();
export const isRtl: boolean = locale === 'ar';

/** Apply document direction (rtl for Arabic). Safe to call once at startup. */
export function applyDir(): void {
  if (isRtl) document.documentElement.dir = 'rtl';
}

/**
 * Translate `key` for the active locale, with `{var}` interpolation.
 * Falls back to Turkish, then to the key itself if unknown.
 */
export function t(key: string, vars?: Record<string, string | number>): string {
  const raw = messages[locale][key] ?? messages.tr[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (m, name: string) =>
    name in vars ? String(vars[name]) : m,
  );
}
